import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserManagementUsersTemplate } from '../../templates';
import { ErrorMolecule } from '../../molecules';
import Spinner from '../../atoms/Spinner/Spinner';
import {
  useGetRoles,
  useInfiniteGetUsers,
} from '../../../store/contextAPI/user/queries/user.queries';
import {
  SortConfig,
  UserSearchFilters,
  UserSortBy,
} from '../../../utils/types/types';
import userService from '../../../store/contextAPI/user/services/user.service';

const UserManagementUsersPage = () => {
  const clientQuery = useQueryClient();
  const [filters, setFilters] = useState<UserSearchFilters>();
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: 'name',
    direction: 'asc',
  });

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status,
    error,
  } = useInfiniteGetUsers({
    searchTerm: filters?.searchTerm,
    roleIds: filters?.roleIds,
    isInactive: filters?.isInactive,
    pageSize: 10,
    sortBy: sortConfig.key as UserSortBy,
    sortDirection: sortConfig.direction,
  });

  const { data: roles } = useGetRoles();

  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!hasNextPage || !loadMoreRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          fetchNextPage();
        }
      },
      { rootMargin: '100px' }
    );

    observer.observe(loadMoreRef.current);

    return () => observer.disconnect();
  }, [hasNextPage, fetchNextPage]);

  const handleSearch = (currentFilters: UserSearchFilters) => {
    setFilters(currentFilters);
  };

  const handleDeleteUser = async (id: string) => {
    return await userService.deleteUser(id);
  };

  const { mutate: deleteUser, isPending } = useMutation({
    mutationFn: handleDeleteUser,
    onSuccess: () =>
      clientQuery.invalidateQueries({
        queryKey: ['users'],
      }),
    onError: (error) => console.log(error),
  });

  if (status === 'pending' || isPending) {
    return <Spinner />;
  }

  if (status === 'error') {
    return <ErrorMolecule content={error.message ?? ''} />;
  }

  const users = data?.pages.flatMap((page) => page.data) ?? [];

  return (
    <UserManagementUsersTemplate
      data={users}
      roles={roles ?? []}
      filters={filters}
      handleDeleteUser={deleteUser}
      handleSearch={handleSearch}
      isFetchingNextPage={isFetchingNextPage}
      loadMoreRef={loadMoreRef}
      sortConfig={sortConfig}
      setSortConfig={setSortConfig}
    />
  );
};

export default UserManagementUsersPage;
